import { FaCoins } from "@react-icons/all-files/fa/FaCoins";
import { memo } from "react";
import styled from "styled-components";

import type { Character } from "@core/types/character";
import { getIsSpecialist } from "@core/utils";

interface Props {
  character: Character;
}

/**
 * 표 뷰의 캐릭터 셀 (sticky left).
 * - 자기 자신도 배경 이미지를 보유 → 가로 스크롤 시 다른 셀 텍스트가 비치지 않도록 불투명 유지
 * - 행(BodyRow)도 동일한 이미지를 자연 폭으로 그려서 셀 경계 밖으로 이미지가 자연스럽게 연장됨
 */
const CharacterCell = ({ character }: Props) => {
  return (
    <Wrapper
      style={{
        backgroundImage:
          character.characterImage !== null
            ? `url(${character.characterImage})`
            : undefined,
        // 카드 뷰(size 150%, 높이 110px, -80/-30)와 동일한 비율 → 표 뷰 높이 90px 기준
        // 가로 30% — size 150%에서 이미지 내 30% 지점이 셀 30% 지점에 맞춰짐 → 피사체가 셀 우측으로 이동
        // 스페셜리스트(기상술사/도화가/환수사)는 얼굴이 이미지 아래쪽이라 추가로 위로 올림
        backgroundPosition: getIsSpecialist(character.characterClassName)
          ? "30% -85px"
          : "30% -35px",
      }}
    >
      <Info>
        <NameRow>
          <Name title={character.characterName}>{character.characterName}</Name>
          {character.goldCharacter && (
            <GoldBadge title="골드 획득 지정">
              <FaCoins />
            </GoldBadge>
          )}
        </NameRow>
        <Meta>
          <ServerName>@{character.serverName}</ServerName>
          <ClassName>{character.characterClassName}</ClassName>
        </Meta>
        <Level>Lv. {character.itemLevel}</Level>
      </Info>
    </Wrapper>
  );
};

export default memo(CharacterCell);

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 90px;
  padding: 0 12px;
  background-color: ${({ theme }) => theme.app.palette.gray[500]};
  /* 카드 뷰와 동일한 150% — 이미지 이동 범위 확보 (115%는 이동 여유 29px뿐이라 position 변경 체감 안 됨) */
  background-size: 150%;
  background-repeat: no-repeat;
  color: ${({ theme }) => theme.app.palette.gray[0]};
  overflow: hidden;

  /* 왼쪽 gradient 흐림 — 텍스트 가독성 */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.78) 0%,
      rgba(0, 0, 0, 0.5) 55%,
      rgba(0, 0, 0, 0.2) 100%
    );
    pointer-events: none;
  }
`;

const Info = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const Name = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const GoldBadge = styled.span`
  color: #ffd24a;
  font-size: 11px;
  flex-shrink: 0;
`;

const Meta = styled.div`
  display: flex;
  gap: 4px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
`;

const ServerName = styled.span`
  white-space: nowrap;
`;

const ClassName = styled.span`
  white-space: nowrap;
`;

const Level = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #ffd24a;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
`;
